import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRight,
  BookOpen,
  Clock,
  Calendar,
  Sparkles,
  Star,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import useSeo from '../hooks/useSeo';
import { buildBreadcrumb, combineJsonLd } from '../seo/breadcrumbs';
import { buildItemList } from '../seo/schemas';
import { MAGAZINE_ARTICLES } from '../data/magazine';
import { OfficialBadge } from '../components/OfficialBadge';
import RelatedLinks from '../components/RelatedLinks';

/**
 * Sommaire du Magazine CODE 2 ROUES — version éditoriale premium.
 *
 * Direction artistique alignée avec la home :
 *   • Hero pattern oblique + kicker rouge + Playfair Display
 *   • Section "Cette semaine" en grand format cinéma asymétrique
 *   • Grille des archives avec filets d'accent par catégorie
 *   • Animations fade-in subtiles, mobile-first
 */

// Palette d'accents par catégorie éditoriale (cohérente avec la home)
const CATEGORY_ACCENT = {
  'Préparation au permis moto': '#cf2031', // rouge primary
  'Sécurité & Équipement': '#f59e0b',      // ambre
  'Sécurité': '#f59e0b',
  'Équipement': '#f59e0b',
  'Conduite': '#0891b2',                   // cyan
  'Réglementation': '#1d4ed8',             // bleu
  'Scolarité': '#16a34a',                  // vert
  'Mobilités urbaines': '#7c3aed',         // violet
  'Préparation': '#cf2031',
};

const accentFor = (cat) => CATEGORY_ACCENT[cat] || '#cf2031';

// Chaque article featured peut déclarer son propre `heroVideo` (chemin local
// vers un .mp4 dans /public/videos/). Si l'article n'a pas de vidéo, le hero
// affiche simplement l'image poster (heroImage) — comportement 100 % rétrocompatible.
// Pour ajouter une vidéo à un article : ajouter `heroVideo: '/videos/xxx.mp4'`
// dans l'objet exporté du fichier data/magazine/xxx.js.

/**
 * Hook : respecte la préférence système « réduire les animations ».
 * Retourne `true` si l'utilisateur a demandé moins de mouvement.
 */
function usePrefersReducedMotion() {
  const [prefers, setPrefers] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const handler = () => setPrefers(!!mq.matches);
    handler();
    mq.addEventListener?.('change', handler);
    return () => mq.removeEventListener?.('change', handler);
  }, []);
  return prefers;
}

export default function MagazineIndex() {
  const navigate = useNavigate();
  const reducedMotion = usePrefersReducedMotion();

  // "À la une" = semaine ISO en cours si publiée, sinon volume le plus récent.
  const getISOWeek = (d = new Date()) => {
    const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
    const dayNum = date.getUTCDay() || 7;
    date.setUTCDate(date.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
    const weekNum = Math.ceil(((date - yearStart) / 86400000 + 1) / 7);
    return { week: weekNum, year: date.getUTCFullYear() };
  };
  const { week: currentWeek, year: currentYear } = getISOWeek();

  // Tri éditorial unifié — du plus récent au plus ancien :
  //   1) Édition en cours (semaine ISO courante) → FEATURED
  //   2) Reste : ordre décroissant par weekVolume (les articles sans volume
  //      ou marqués isGuide passent en fin de liste comme archives permanentes)
  const { featured, rest } = useMemo(() => {
    const all = [...MAGAZINE_ARTICLES].sort((a, b) => {
      const aIsCurrent = a.weekVolume === currentWeek && a.weekYear === currentYear;
      const bIsCurrent = b.weekVolume === currentWeek && b.weekYear === currentYear;
      if (aIsCurrent && !bIsCurrent) return -1;
      if (bIsCurrent && !aIsCurrent) return 1;
      // Articles sans volume (guides permanents) → en fin de liste
      const aVol = a.weekVolume ?? -Infinity;
      const bVol = b.weekVolume ?? -Infinity;
      return bVol - aVol;
    });
    const feat = all[0] || null;
    return {
      featured: feat,
      rest: all.filter((a) => a.slug !== feat?.slug),
    };
  }, [currentWeek, currentYear]);

  useSeo({
    title: 'Magazine — Tous les conseils ECSR pour motards',
    description:
      'Le magazine hebdomadaire CODE 2 ROUES : conseils, dossiers et guides ECSR pour motards. Casque, gants, ZFE, distances, alcool… Un nouvel article chaque semaine.',
    keywords: 'magazine moto, conseil ECSR, dossier moto, guide motard, code moto pratique, casque moto, gants moto, distances securite, ZFE moto, alcool moto',
    path: '/magazine',
    image: featured?.heroImage,
    preloadImage: featured?.heroImage,
    jsonLd: combineJsonLd(
      buildBreadcrumb([['Magazine', '/magazine']]),
      buildItemList({
        name: 'Articles & guides du Magazine CODE 2 ROUES',
        items: MAGAZINE_ARTICLES.slice(0, 30).map((a) => ({
          name: a.title,
          path: `/magazine/${a.slug}`,
          description: a.excerpt || a.subtitle || a.category,
        })),
      }),
    ),
  });

  return (
    <div className="space-y-10 sm:space-y-16 lg:space-y-20" data-testid="magazine-index-page">
      {/* ====================================================================
          HERO MAGAZINE — pattern oblique + kicker rouge + titre Playfair XXL
          ==================================================================== */}
      <section
        className="relative overflow-hidden rounded-2xl bg-card hero-content-fade"
        data-testid="magazine-index-hero"
        style={{
          background:
            'radial-gradient(ellipse 70% 60% at 100% 0%, rgba(207, 32, 49, 0.08), transparent 65%), hsl(var(--card))',
        }}
      >
        {/* Pattern oblique très subtil */}
        <div
          className="absolute inset-0 opacity-[0.03] pointer-events-none"
          style={{
            backgroundImage:
              'repeating-linear-gradient(45deg, transparent 0 12px, hsl(var(--foreground)) 12px 13px)',
          }}
        />

        <div className="relative px-6 py-10 sm:px-10 sm:py-14 lg:px-14 lg:py-16">
          <div className="flex items-center gap-2 mb-4">
            <span className="w-1.5 h-1.5 rounded-full bg-[hsl(var(--primary))]" />
            <span className="text-[10px] sm:text-[11px] font-black uppercase tracking-[0.18em] text-[hsl(var(--primary))]">
              Magazine hebdomadaire · ECSR 2026
            </span>
          </div>

          <h1
            className="font-extrabold tracking-tight text-foreground max-w-4xl leading-[0.98]"
            style={{
              fontFamily: "'Playfair Display', 'Crimson Text', Georgia, serif",
              fontSize: 'clamp(2.4rem, 6.5vw, 5rem)',
            }}
          >
            Le magazine{' '}
            <span className="text-[hsl(var(--primary))] italic font-medium">
              conseils ECSR
            </span>
            .
          </h1>

          <p
            className="text-sm sm:text-base lg:text-lg text-foreground/70 mt-5 max-w-2xl leading-relaxed italic"
            style={{ fontFamily: "'Crimson Text', Georgia, serif" }}
          >
            Chaque semaine, un nouveau dossier composé par des moniteurs ECSR :
            casque, gants, distances, ZFE, alcool, autoroute, EPI… Références
            officielles + mini-quiz d'ancrage à chaque article.
          </p>

          <div className="mt-6 flex flex-wrap items-center gap-2.5">
            <OfficialBadge size="md" />
            <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-background/60 backdrop-blur-sm border border-border/40 text-[10px] sm:text-[11px] font-bold text-foreground/70 gap-1">
              <Sparkles className="w-3 h-3 text-[hsl(var(--primary))]" />
              {MAGAZINE_ARTICLES.length} articles publiés
            </span>
          </div>
        </div>
      </section>

      {/* ====================================================================
          CETTE SEMAINE — Hero cinématique vivant (Ken Burns + grain + marquee)
          ==================================================================== */}
      {featured && (
        <section data-testid="magazine-featured-section">
          <div className="flex items-end justify-between mb-4 sm:mb-5 gap-3 flex-wrap">
            <div>
              <div className="flex items-center gap-2 mb-1.5">
                {/* Point rouge pulsant "LIVE" */}
                <span className="relative flex h-2 w-2" aria-hidden="true">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[hsl(var(--primary))] opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-[hsl(var(--primary))]" />
                </span>
                <span className="text-[10px] sm:text-[11px] font-black uppercase tracking-[0.18em] text-[hsl(var(--primary))]">
                  En direct · À la une · Volume {featured.weekVolume} · {featured.monthLabel}
                </span>
              </div>
              <h2
                className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-foreground"
                style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
              >
                Cette semaine
              </h2>
            </div>
            {/* Badge droite : édition en cours */}
            <div className="hidden sm:flex items-center gap-2">
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-foreground/[0.04] border border-border/40 text-[11px] font-semibold text-foreground/70">
                <Calendar className="w-3 h-3 text-[hsl(var(--primary))]" />
                Édition {featured.weekVolume}/52 · 2026
              </span>
            </div>
          </div>

          <button
            type="button"
            onClick={() => navigate(`/magazine/${featured.slug}`)}
            className="group relative w-full overflow-hidden rounded-2xl bg-card transition-all duration-500 hover:scale-[1.005] active:scale-[0.995] focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--primary))]"
            style={{ boxShadow: '0 20px 60px -20px rgba(0,0,0,0.65), 0 8px 24px -12px rgba(207,32,49,0.15)' }}
            data-testid="magazine-featured"
            aria-label={`Lire l'article : ${featured.title}`}
          >
            <div className="grid lg:grid-cols-[1.3fr_1fr] min-h-[460px] sm:min-h-[520px] lg:min-h-[560px]">
              {/* Fond vidéo cinéma + Ken Burns + grain */}
              <div className="relative min-h-[280px] lg:min-h-full overflow-hidden order-1 lg:order-1 c2r-noise">
                {reducedMotion || !featured.heroVideo ? (
                  // Mode "réduire les animations" OU article sans vidéo : image seule
                  <img
                    src={featured.heroImage}
                    alt={featured.heroImageAlt || featured.title}
                    className={`absolute inset-0 w-full h-full object-cover ${reducedMotion ? '' : 'c2r-kenburns'}`}
                    loading="eager"
                    fetchpriority="high"
                    style={{ filter: 'saturate(0.95) contrast(1.06)' }}
                  />
                ) : (
                  <>
                    {/* Vidéo de fond propre à l'article — autoplay silencieux en boucle.
                        Poster = heroImage de l'article : s'affiche INSTANTANÉMENT
                        pendant le chargement et reste en cas d'échec réseau. */}
                    <video
                      key={featured.slug /* force re-mount quand on change d'article */}
                      className="absolute inset-0 w-full h-full object-cover c2r-kenburns"
                      autoPlay
                      loop
                      muted
                      playsInline
                      preload="metadata"
                      poster={featured.heroImage}
                      aria-hidden="true"
                      style={{ filter: 'saturate(0.95) contrast(1.06)' }}
                    >
                      <source src={featured.heroVideo} type="video/mp4" />
                      {/* Fallback image si <video> non supporté (ancien navigateur) */}
                      <img
                        src={featured.heroImage}
                        alt={featured.heroImageAlt || featured.title}
                        className="absolute inset-0 w-full h-full object-cover"
                      />
                    </video>
                    <span className="sr-only">{featured.heroImageAlt || featured.title}</span>
                  </>
                )}
                {/* Dégradé cinéma bottom */}
                <div
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    background:
                      'linear-gradient(180deg, rgba(15,12,8,0.10) 0%, rgba(15,12,8,0.30) 55%, rgba(15,12,8,0.92) 100%)',
                  }}
                />
                {/* Fondu latéral vers le bloc texte (desktop) */}
                <div
                  className="absolute inset-0 pointer-events-none hidden lg:block"
                  style={{
                    background:
                      'linear-gradient(90deg, transparent 0%, transparent 68%, hsl(var(--card)) 100%)',
                  }}
                />
                {/* Scanline subtile pour effet cinéma */}
                <div
                  className="absolute inset-0 pointer-events-none opacity-[0.06] mix-blend-overlay"
                  style={{
                    backgroundImage:
                      'repeating-linear-gradient(0deg, transparent 0 2px, #000 2px 3px)',
                  }}
                  aria-hidden="true"
                />

                {/* Badge "LIVE À LA UNE" top-left avec point pulsant */}
                <div className="absolute top-4 left-4 flex items-center gap-2 z-10">
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[hsl(var(--primary))] text-white text-[10px] font-black uppercase tracking-[0.14em] shadow-lg backdrop-blur-md">
                    <span className="relative flex h-1.5 w-1.5" aria-hidden="true">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" />
                      <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-white" />
                    </span>
                    À la une
                  </span>
                  <span className="hidden sm:inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full bg-black/50 backdrop-blur-md text-white/90 text-[10px] font-black uppercase tracking-[0.12em] border border-white/10">
                    <Calendar className="w-3 h-3" aria-hidden="true" />
                    Semaine {featured.weekVolume}
                  </span>
                </div>

                {/* Chrono bas-droite : temps lecture */}
                <div className="absolute bottom-4 right-4 flex items-center gap-2 z-10">
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-black/55 backdrop-blur-md text-white text-[11px] font-black uppercase tracking-[0.08em] border border-white/10">
                    <Clock className="w-3 h-3" aria-hidden="true" />
                    {featured.readTimeMin || 4} min de lecture
                  </span>
                </div>

                {/* Titre overlay sur image (mobile uniquement, caché desktop) */}
                <div className="absolute bottom-16 left-4 right-4 lg:hidden z-10">
                  <span
                    className="inline-block text-[10px] font-black uppercase tracking-[0.18em] px-2 py-1 rounded"
                    style={{ background: accentFor(featured.category), color: '#fff' }}
                  >
                    {featured.category}
                  </span>
                </div>
              </div>

              {/* Bloc texte droite */}
              <div className="relative px-6 py-7 sm:px-9 sm:py-10 lg:px-12 lg:py-14 flex flex-col justify-center order-2 lg:order-2 bg-card">
                {/* Filet d'accent vertical */}
                <span
                  className="absolute left-0 top-8 bottom-8 w-[3px] hidden lg:block rounded-r"
                  style={{ background: accentFor(featured.category) }}
                  aria-hidden="true"
                />

                <div className="flex items-center gap-2 mb-3 flex-wrap">
                  <span
                    className="text-[10px] sm:text-[11px] font-black uppercase tracking-[0.18em]"
                    style={{ color: accentFor(featured.category) }}
                  >
                    {featured.category}
                  </span>
                  <span className="text-[10px] text-foreground/40">·</span>
                  <span className="text-[10px] sm:text-[11px] text-foreground/55 font-medium italic inline-flex items-center gap-1">
                    <Clock className="w-3 h-3" /> {featured.readTimeMin || 4} min
                  </span>
                  <span className="text-[10px] text-foreground/40">·</span>
                  <span className="text-[10px] sm:text-[11px] text-foreground/55 font-medium italic">
                    Bruno · Équipe Code 2 Roues
                  </span>
                </div>

                <h3
                  className="font-extrabold tracking-tight text-foreground leading-[1.04]"
                  style={{
                    fontFamily: "'Playfair Display', Georgia, serif",
                    fontSize: 'clamp(1.7rem, 3.6vw, 2.7rem)',
                  }}
                >
                  {featured.title}
                </h3>

                {featured.subtitle && (
                  <p
                    className="text-sm sm:text-[15px] text-foreground/70 leading-relaxed mt-3 italic line-clamp-3"
                    style={{ fontFamily: "'Crimson Text', Georgia, serif" }}
                    dangerouslySetInnerHTML={{ __html: featured.subtitle }}
                  />
                )}

                {/* Pastilles de chapitres si disponibles */}
                {Array.isArray(featured.sections) && featured.sections.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-5" aria-label="Au sommaire de l'article">
                    {featured.sections.slice(0, 4).map((s, i) => (
                      <span
                        key={i}
                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-foreground/[0.04] border border-border/40 text-[10px] font-bold text-foreground/70 group-hover:border-[hsl(var(--primary))]/40 group-hover:text-foreground transition-colors"
                      >
                        <span className="w-1 h-1 rounded-full" style={{ background: accentFor(featured.category) }} />
                        {s.part || s.title}
                      </span>
                    ))}
                    {featured.sections.length > 4 && (
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-bold text-foreground/55">
                        +{featured.sections.length - 4}
                      </span>
                    )}
                  </div>
                )}

                <div className="flex flex-wrap items-center gap-3 mt-6">
                  <span
                    className="c2r-pulse-glow inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full text-white text-[11px] font-black uppercase tracking-[0.10em] group-hover:opacity-95 transition-opacity shadow-lg"
                    style={{ background: accentFor(featured.category) }}
                  >
                    Lire l'article complet
                    <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" />
                  </span>
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-background/60 border border-border/40 text-[10px] font-bold text-foreground/65">
                    <Sparkles className="w-3 h-3 text-[hsl(var(--primary))]" />
                    + Quiz d'ancrage
                  </span>
                </div>
              </div>
            </div>

            {/* Marquee défilant bas — métadonnées éditoriales */}
            <div
              className="c2r-marquee relative overflow-hidden border-t border-border/30 bg-foreground/[0.015]"
              aria-hidden="true"
            >
              <div className="c2r-marquee__track py-2.5">
                {[0, 1, 2].map((loop) => (
                  <React.Fragment key={loop}>
                    <span className="c2r-marquee__item">
                      <Clock className="w-3 h-3" />
                      {featured.readTimeMin || 4} min de lecture
                    </span>
                    <span className="c2r-marquee__sep">·</span>
                    <span className="c2r-marquee__item">
                      <Star className="w-3 h-3" />
                      {featured.category}
                    </span>
                    <span className="c2r-marquee__sep">·</span>
                    <span className="c2r-marquee__item">
                      <Calendar className="w-3 h-3" />
                      Semaine ISO {featured.weekVolume} · {featured.monthLabel}
                    </span>
                    <span className="c2r-marquee__sep">·</span>
                    <span className="c2r-marquee__item" style={{ color: 'hsl(var(--primary))' }}>
                      ● Édition en cours
                    </span>
                    <span className="c2r-marquee__sep">·</span>
                    <span className="c2r-marquee__item">
                      <BookOpen className="w-3 h-3" />
                      Bruno · Équipe Code 2 Roues
                    </span>
                    <span className="c2r-marquee__sep">·</span>
                    <span className="c2r-marquee__item">
                      <Sparkles className="w-3 h-3" />
                      Quiz d'ancrage inclus
                    </span>
                    <span className="c2r-marquee__sep">·</span>
                  </React.Fragment>
                ))}
              </div>
            </div>
          </button>
        </section>
      )}

      {/* ====================================================================
          ARCHIVES — Grille avec accents colorés par catégorie
          ==================================================================== */}
      {rest.length > 0 && (
        <ArchivesSection rest={rest} navigate={navigate} />
      )}

      {/* ====================================================================
          CTA bas de page — appel à la révision
          ==================================================================== */}
      <section
        className="relative overflow-hidden rounded-2xl bg-card text-center px-6 py-10 sm:px-10 sm:py-14"
        data-testid="magazine-cta-bottom"
        style={{
          background:
            'radial-gradient(ellipse 60% 60% at 50% 100%, rgba(207, 32, 49, 0.08), transparent 65%), hsl(var(--card))',
        }}
      >
        <div className="flex items-center justify-center gap-2 mb-3">
          <span className="w-1.5 h-1.5 rounded-full bg-[hsl(var(--primary))]" />
          <span className="text-[10px] sm:text-[11px] font-black uppercase tracking-[0.18em] text-[hsl(var(--primary))]">
            Passe à la pratique
          </span>
        </div>
        <h3
          className="text-2xl sm:text-3xl font-extrabold tracking-tight text-foreground mb-3 max-w-xl mx-auto leading-[1.05]"
          style={{
            fontFamily: "'Playfair Display', Georgia, serif",
          }}
        >
          Envie d'aller plus loin ?
        </h3>
        <p className="text-sm text-foreground/65 mb-6 max-w-xl mx-auto leading-relaxed italic"
          style={{ fontFamily: "'Crimson Text', Georgia, serif" }}>
          Chaque dossier est complété par un mini-quiz d'ancrage. Explore tous les
          dossiers, enquêtes et livrets — 100 % gratuits, rédigés par des moniteurs
          ECSR.
        </p>
        <Button
          onClick={() => navigate('/revision')}
          className="mc-btn-primary"
          data-testid="magazine-cta-revision"
        >
          Explorer les dossiers <ArrowRight className="w-4 h-4 ml-1.5" />
        </Button>
      </section>

      {/* MAILLAGE INTERNE — boost SEO + temps de session */}
      <RelatedLinks topic="etm" max={5} />
    </div>
  );
}

/* ==========================================================================
   ARCHIVES SECTION avec filtres par thème
   ========================================================================== */
const THEMES = [
  { id: 'all', label: 'Tous', match: () => true },
  {
    id: 'technique',
    label: 'Technique',
    match: (a) => /technique|conduite|grand route/i.test(a.category || ''),
  },
  {
    id: 'equipement',
    label: 'Équipement',
    match: (a) => /équipement|equipement|équipements/i.test(a.category || ''),
  },
  {
    id: 'barometres',
    label: 'Baromètres',
    match: (a) =>
      /baro|actualité|actualite|sécurité routière|securite routiere/i.test(
        a.category || '',
      ),
  },
  {
    id: 'plateau',
    label: 'Plateau & permis',
    match: (a) =>
      /plateau|permis|examen|épreuves|epreuves|pédagogie|pedagogie|assr/i.test(
        a.category || '',
      ),
  },
];

const MAGAZINE_FILTER_KEY = 'c2r.magazine.activeTheme';

function ArchivesSection({ rest, navigate }) {
  // Filtre persistant : on hydrate depuis localStorage au premier render.
  const [activeTheme, setActiveTheme] = useState(() => {
    if (typeof window === 'undefined') return 'all';
    try {
      const saved = window.localStorage.getItem(MAGAZINE_FILTER_KEY);
      // On vérifie que la valeur sauvée correspond à un thème valide
      if (saved && THEMES.some((t) => t.id === saved)) return saved;
    } catch {
      /* localStorage indisponible (privé, désactivé) — on ignore */
    }
    return 'all';
  });

  // Sauvegarde à chaque changement de filtre
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(MAGAZINE_FILTER_KEY, activeTheme);
    } catch {
      /* localStorage indisponible — on ignore */
    }
  }, [activeTheme]);

  const themeCount = useMemo(() => {
    const map = {};
    THEMES.forEach((t) => {
      map[t.id] = rest.filter(t.match).length;
    });
    return map;
  }, [rest]);

  const filtered = useMemo(() => {
    const theme = THEMES.find((t) => t.id === activeTheme) || THEMES[0];
    return rest.filter(theme.match);
  }, [rest, activeTheme]);

  return (
    <section data-testid="magazine-archives-section">
      <div className="flex items-end justify-between mb-4 sm:mb-5 gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 mb-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-[hsl(var(--primary))]" />
            <span className="text-[10px] sm:text-[11px] font-black uppercase tracking-[0.18em] text-[hsl(var(--primary))]">
              Archives · {filtered.length}
              {activeTheme !== 'all' && ` / ${rest.length}`} dossiers
            </span>
          </div>
          <h2
            className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-foreground"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
          >
            Tous les conseils
          </h2>
        </div>
      </div>

      {/* Filtres par thème — scrollables horizontalement sur mobile */}
      <div
        className="flex gap-2 mb-5 sm:mb-6 overflow-x-auto pb-2 -mx-4 px-4 sm:mx-0 sm:px-0 sm:flex-wrap scrollbar-none"
        data-testid="magazine-theme-filters"
        role="tablist"
        aria-label="Filtrer les dossiers par thème"
      >
        {THEMES.map((t) => {
          const count = themeCount[t.id] || 0;
          if (t.id !== 'all' && count === 0) return null;
          const active = activeTheme === t.id;
          return (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => setActiveTheme(t.id)}
              data-testid={`magazine-filter-${t.id}`}
              className={`shrink-0 inline-flex items-center gap-1.5 h-9 px-4 rounded-full text-[12px] font-black uppercase tracking-[0.06em] transition-all border ${
                active
                  ? 'bg-[hsl(var(--primary))] text-white border-[hsl(var(--primary))] shadow-md'
                  : 'bg-card text-foreground/70 border-border/50 hover:border-[hsl(var(--primary))]/50 hover:text-foreground'
              }`}
            >
              {t.label}
              <span
                className={`text-[10px] font-bold ${
                  active ? 'text-white/80' : 'text-foreground/40'
                }`}
              >
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {filtered.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border/50 py-10 text-center text-sm text-foreground/60">
          Aucun dossier dans cette catégorie pour le moment.
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
          {filtered.map((a) => {
            const accent = accentFor(a.category);
            return (
              <button
                key={a.slug}
                type="button"
                onClick={() => navigate(`/magazine/${a.slug}`)}
                data-testid={`magazine-card-${a.slug}`}
                aria-label={`Lire l'article : ${a.title}`}
                className="group text-left relative overflow-hidden rounded-xl bg-card border border-border/40 hover:border-[hsl(var(--primary))]/40 transition-all duration-300 hover:scale-[1.015] active:scale-[0.99] focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--primary))] flex flex-col"
                style={{ boxShadow: '0 6px 18px -12px rgba(0,0,0,0.4)' }}
              >
                <span
                  className="absolute top-0 inset-x-0 h-[3px] z-10"
                  style={{ background: accent }}
                />
                <div className="relative h-44 sm:h-48 overflow-hidden">
                  <img
                    src={a.heroImage}
                    alt={a.heroImageAlt || a.title}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    loading="lazy"
                    style={{ filter: 'saturate(0.92) contrast(1.04)' }}
                  />
                  <div
                    className="absolute inset-0 pointer-events-none"
                    style={{
                      background:
                        'linear-gradient(180deg, rgba(15,12,8,0.10) 0%, rgba(15,12,8,0.55) 100%)',
                    }}
                  />
                  <span
                    className="absolute top-3 left-3 inline-flex items-center px-2.5 py-1 rounded-full text-white text-[9px] font-black uppercase tracking-[0.14em] backdrop-blur-md shadow"
                    style={{
                      background: a.isGuide
                        ? 'hsl(var(--primary) / 0.92)'
                        : 'rgba(15,12,8,0.65)',
                    }}
                  >
                    {a.isGuide ? (
                      <>
                        <Star className="w-2.5 h-2.5 fill-current mr-1" />
                        Guide ECSR
                      </>
                    ) : (
                      `Vol. ${a.weekVolume}`
                    )}
                  </span>
                </div>
                <div className="p-4 sm:p-5 flex-1 flex flex-col">
                  <div className="flex items-center gap-2 mb-2">
                    <span
                      className="text-[10px] font-black uppercase tracking-[0.16em]"
                      style={{ color: accent }}
                    >
                      {a.category}
                    </span>
                    <span className="text-[10px] text-foreground/35">·</span>
                    <span className="text-[10px] text-foreground/55 font-medium italic inline-flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {a.monthLabel}
                    </span>
                  </div>
                  <h3
                    className="text-lg sm:text-xl font-extrabold tracking-tight text-foreground leading-tight mb-2"
                    style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
                  >
                    {a.title}
                  </h3>
                  <p className="text-xs text-foreground/60 leading-relaxed line-clamp-2 mb-3">
                    {a.subtitle}
                  </p>
                  <div className="mt-auto flex items-center justify-between gap-2 pt-2 border-t border-border/30">
                    <span
                      className="inline-flex items-center gap-1 text-[11px] font-black uppercase tracking-[0.10em] transition-transform group-hover:translate-x-0.5"
                      style={{ color: accent }}
                    >
                      Lire <ArrowRight className="w-3 h-3" />
                    </span>
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold text-foreground/55">
                      <Clock className="w-2.5 h-2.5" />
                      {a.readTimeMin || 4} min
                    </span>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </section>
  );
}
